import React, { useCallback, useMemo, useState } from "react";

import { FormattedMessage, IntlShape, MessageDescriptor, useIntl } from "react-intl";

import { Modal, SearchField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import ToggleEntityCheckbox from "@app/component/input/ToggleEntityCheckbox";

import useMutableSet from "../../util/hook/useMutableSet";

import styles from "./selectEntityDialogFactory.module.scss";
import defaultFilter from "./util/defaultFilter";
import getDefaultAllSelectCaption from "./util/useDefaultAllSelectCaption";

export interface SelectEntityDialogProps<T = unknown> {
  cancelButtonLabel?: string;
  className?: string;
  confirmButtonLabel?: string;
  entities: T[];
  filter?: ((entities: T[], queryString: string) => T[]) | null;
  focusOnConfirm?: boolean;
  getDisplayName: (entity: T) => string;
  getKey: (entity: T) => string;
  id?: string;
  modal?: boolean;
  onCancel?: () => void;
  onConfirm?: (selectedEntities: T[]) => void;
  onHide: () => void;
  selectedEntities?: T[];
  title: string;
  visible: boolean;
}

const selectEntityDialogFactory =
  <T,>(
    noEntitiesMessage: MessageDescriptor,
    getSelectAllMessage: (
      allSelected: boolean,
      isFiltering: boolean,
      intl: IntlShape
    ) => string = getDefaultAllSelectCaption
  ): React.FC<SelectEntityDialogProps<T>> =>
  ({
    cancelButtonLabel,
    confirmButtonLabel,
    entities,
    filter,
    getDisplayName,
    getKey,
    onCancel,
    onConfirm,
    onHide,
    selectedEntities = [],
    title,
    visible,
  }: SelectEntityDialogProps<T>) => {
    // filtering-related stuff
    const intl = useIntl();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const handleChangeSearchQuery = useCallback((value) => setSearchQuery(value), [setSearchQuery]);
    const filteredEntities = useMemo(() => {
      if (filter === null) {
        return entities;
      }
      return filter
        ? filter(entities, searchQuery)
        : defaultFilter(entities, searchQuery, getDisplayName);
    }, [entities, filter, searchQuery, getDisplayName]);

    // checking-related stuff
    const [checkedEntities, updateCheckedEntities, handleToggleEntity] = useMutableSet<T>(
      () => new Set(selectedEntities)
    );

    const handleSelectAllCheckboxChange = useCallback(
      (checked) =>
        updateCheckedEntities((set) => {
          filteredEntities.forEach((entity) => {
            if (checked) {
              set.add(entity);
            } else {
              set.delete(entity);
            }
          });
          return true;
        }),
      [filteredEntities, updateCheckedEntities]
    );

    const allSelected = filteredEntities.every((entity) => checkedEntities.has(entity));
    const isFiltering = filteredEntities.length < entities.length;
    const selectAllLabel = getSelectAllMessage(allSelected, isFiltering, intl);

    const handleConfirmClick = useCallback(() => {
      onHide();
      onConfirm?.(Array.from(checkedEntities));
    }, [onConfirm, onHide, checkedEntities]);

    return (
      <Modal
        cancelText={
          cancelButtonLabel ??
          intl.formatMessage({
            id: "selectEntityDialog.default.cancelButton",
            defaultMessage: "Cancel",
            description:
              "Placeholder for cancel button text for modal in select entity dialog factory",
          })
        }
        okText={
          confirmButtonLabel ??
          intl.formatMessage({
            id: "selectEntityDialog.default.okButton",
            defaultMessage: "Done",
            description: "Placeholder for ok button text for modal in select entity dialog factory",
          })
        }
        onCancel={onCancel || onHide}
        onOk={handleConfirmClick}
        title={title}
        visible={visible}
      >
        <Checkbox
          checked={allSelected}
          indeterminate={
            allSelected ? false : filteredEntities.some((entity) => checkedEntities.has(entity))
          }
          onChange={handleSelectAllCheckboxChange}
        >
          {selectAllLabel}
        </Checkbox>
        {filter !== null && (
          <SearchField
            className={styles.searchInput}
            onChange={handleChangeSearchQuery}
            placeholder={intl.formatMessage({
              id: "selectEntityDialog.default.search.placeholder",
              defaultMessage: "Search",
              description: "Placeholder for search select entity dialog factory",
            })}
            value={searchQuery}
          />
        )}
        <div className={styles.container}>
          {filteredEntities.map((entity) => (
            <ToggleEntityCheckbox
              checked={checkedEntities.has(entity)}
              displayName={getDisplayName(entity)}
              entity={entity}
              key={getKey(entity)}
              onToggle={handleToggleEntity}
            />
          ))}
          {!filteredEntities.length && (
            <div className={styles.noEntityMessage}>
              <div className={styles.noEntityMessage}>
                <FormattedMessage {...noEntitiesMessage} />
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

export default selectEntityDialogFactory;
